"use client";

/**
 * AI 비서 답변 안에 임베드되는 카드 4종.
 * 2026-05-12 — 텍스트 압축 + 시각화 UX.
 */
import Link from "next/link";
import type {
  CardsBlock,
  TrendCardItem,
  SupplierCardItem,
  RecipeCardItem,
  StoreKpiCardItem,
} from "@/lib/chatMarkers";

const STAGE_META: Record<TrendCardItem["stage"], { label: string; color: string; bg: string }> = {
  emerging: { label: "태동기", color: "#4ADE80", bg: "rgba(74,222,128,0.15)" },
  growing: { label: "성장기", color: "#FF5C35", bg: "rgba(255,92,53,0.15)" },
  mature: { label: "성숙기", color: "#60A5FA", bg: "rgba(96,165,250,0.15)" },
  declining: { label: "하락기", color: "#6B7280", bg: "rgba(107,114,128,0.15)" },
};

export function ChatCards({ block }: { block: CardsBlock }) {
  if (block.type === "trend") {
    return (
      <div className="mt-2 space-y-1.5 max-w-[85%]">
        {block.items.slice(0, 5).map((it) => (
          <TrendCard key={it.id} item={it} />
        ))}
      </div>
    );
  }
  if (block.type === "supplier") {
    return (
      <div className="mt-2 max-w-[95%]">
        <SupplierTable items={block.items.slice(0, 8)} />
      </div>
    );
  }
  if (block.type === "recipe") {
    return (
      <div className="mt-2 space-y-1.5 max-w-[85%]">
        {block.items.slice(0, 4).map((it) => (
          <RecipeCard key={it.id} item={it} />
        ))}
      </div>
    );
  }
  if (block.type === "store_kpi") {
    return (
      <div className="mt-2 max-w-[85%]">
        {block.items.slice(0, 1).map((it, i) => (
          <StoreKpiCard key={i} item={it} />
        ))}
      </div>
    );
  }
  return null;
}

function TrendCard({ item }: { item: TrendCardItem }) {
  const meta = STAGE_META[item.stage] ?? STAGE_META.emerging;
  return (
    <Link
      href={`/trend/${item.id}`}
      className="block card hover:border-primary/40 transition-colors px-3 py-2"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-bold text-text truncate">#{item.keyword}</span>
        <span
          className="text-2xs font-bold px-1.5 py-0.5 rounded-full shrink-0"
          style={{ color: meta.color, background: meta.bg }}
        >
          {meta.label}
        </span>
      </div>
      <div className="text-2xs text-text-sub flex items-center gap-2 flex-wrap">
        <span>점수 {item.score.toFixed(1)}</span>
        {item.mention_30d != null && <span>· 30d {item.mention_30d.toLocaleString()}</span>}
        {item.mention_7d != null && <span>· 7d {item.mention_7d.toLocaleString()}</span>}
      </div>
    </Link>
  );
}

// 2026-05-18 — /manufacturers + 트렌드 시그널과 동일한 테이블 패턴 (헤더 1줄 + 행 N줄).
// 클릭 = product_url 새 탭, 없으면 비활성. 옛 chat history (company/...) 는 분리 렌더.
function SupplierTable({ items }: { items: SupplierCardItem[] }) {
  const newItems = items.filter((it) => it.name);
  const legacyItems = items.filter((it) => !it.name && it.company);
  return (
    <div className="space-y-2">
      {newItems.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_38px] gap-1.5 px-2 py-1.5 text-[10px] font-bold text-text-sub bg-bg-elevated border-b border-border">
            <span>제품</span>
            <span>공급처</span>
            <span className="text-center">이동</span>
          </div>
          {newItems.map((it) => {
            const hasUrl = !!it.product_url;
            const onMove = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (hasUrl) window.open(it.product_url!, "_blank", "noopener,noreferrer");
            };
            const productLabel = it.spec && it.name && !it.name.includes(it.spec) ? `${it.name}, ${it.spec}` : it.name;
            return (
              <div
                key={it.id}
                className="grid grid-cols-[2fr_1fr_38px] gap-1.5 items-center px-2 py-2 text-xs border-b border-border last:border-b-0"
              >
                <span className="font-semibold text-text truncate" title={productLabel}>
                  {productLabel}
                </span>
                <span className="text-2xs text-text-sub truncate" title={it.manufacturer_company}>
                  {it.manufacturer_company}
                  {it.premium && <span className="ml-1 text-primary">⭐</span>}
                </span>
                {hasUrl ? (
                  <button
                    onClick={onMove}
                    className="text-[10px] font-bold text-primary hover:underline text-center"
                    title="새 탭에서 제품 페이지 열기"
                  >
                    이동 ↗
                  </button>
                ) : (
                  <span className="text-[10px] text-text-sub/40 text-center">-</span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* 옛 chat history (~2026-05-17) 호환 — 공급처 단위 카드는 별도 묶음으로 */}
      {legacyItems.map((it) => (
        <LegacySupplierCard key={`legacy-${it.id}`} item={it} />
      ))}
    </div>
  );
}

function LegacySupplierCard({ item }: { item: SupplierCardItem }) {
  return (
    <Link
      href={`/manufacturers/${item.id}`}
      className={`block card hover:border-primary/40 transition-colors px-3 py-2 ${
        item.premium ? "border-primary/40 bg-primary/5" : ""
      }`}
    >
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        {(item.categories || []).slice(0, 3).map((c) => (
          <span key={c} className="text-2xs font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
            {c}
          </span>
        ))}
        {item.premium && <span className="text-2xs font-bold text-primary">⭐ 프리미엄</span>}
      </div>
      <p className="text-sm font-bold text-text truncate">{item.company}</p>
      {item.summary && (
        <p className="text-2xs text-text-sub mt-0.5 line-clamp-2 leading-snug">🤖 {item.summary}</p>
      )}
      {item.region && <p className="text-2xs text-text-sub mt-0.5">📍 {item.region}</p>}
    </Link>
  );
}

function RecipeCard({ item }: { item: RecipeCardItem }) {
  return (
    <Link
      href={`/recipe/${item.id}`}
      className="block card hover:border-primary/40 transition-colors px-3 py-2"
    >
      <div className="flex gap-2 items-start">
        {item.thumbnail_url && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={item.thumbnail_url}
            alt={item.title}
            className="w-16 h-16 rounded object-cover shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-text line-clamp-2 leading-snug">🍳 {item.title}</p>
          <div className="text-2xs text-text-sub mt-1 flex items-center gap-2 flex-wrap">
            {item.channel && <span className="truncate">{item.channel}</span>}
            {item.view_count != null && (
              <span>· 조회 {item.view_count.toLocaleString()}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function StoreKpiCard({ item }: { item: StoreKpiCardItem }) {
  const change = item.change_pct;
  const changeColor =
    change == null
      ? "text-text-sub"
      : change > 0
      ? "text-emerald-500"
      : change < 0
      ? "text-red-500"
      : "text-text-sub";
  const changeText =
    change == null
      ? "지난 주 데이터 없음"
      : change > 0
      ? `↑ ${change.toFixed(1)}%`
      : change < 0
      ? `↓ ${Math.abs(change).toFixed(1)}%`
      : "± 0%";
  return (
    <Link
      href="/store/dashboard"
      className="block card hover:border-primary/40 transition-colors px-3 py-2"
    >
      <p className="text-2xs text-text-sub">🏪 {item.store_name} · 이번 주 매출</p>
      <p className="text-lg font-bold text-text mt-0.5">
        {item.this_week_revenue.toLocaleString()}
      </p>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <span className={`text-2xs font-semibold ${changeColor}`}>{changeText}</span>
        {item.top_menu && (
          <span className="text-2xs text-text-sub">· TOP {item.top_menu}</span>
        )}
      </div>
    </Link>
  );
}
